import { UserRole, UserPreferences } from '@visitproof/types';

const ACTIVE_ROLE_STORAGE_KEY = 'visitproof_active_role';

interface StoredActiveRoles {
  [accountId: string]: UserRole;
}

/**
 * Get active roles from localStorage
 */
export function getStoredActiveRoles(): StoredActiveRoles {
  try {
    const stored = localStorage.getItem(ACTIVE_ROLE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Store active role for an account in localStorage
 */
export function setStoredActiveRole(accountId: string, role: UserRole): void {
  try {
    const stored = getStoredActiveRoles();
    stored[accountId] = role;
    localStorage.setItem(ACTIVE_ROLE_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Resolve the active role for a user based on:
 * 1. localStorage (client-side preference)
 * 2. Server-side preference (activeRoleByAccount in UserPreferences)
 * 3. claims.role (default from backend)
 *
 * The resolved role is validated against availableRoles to ensure it's valid.
 */
export function resolveActiveRole(
  accountId: string | null,
  availableRoles: UserRole[],
  claimsRole: UserRole | null,
  preferences?: UserPreferences | null
): UserRole | null {
  if (!accountId || availableRoles.length === 0) {
    return claimsRole;
  }

  // 1. Check localStorage first (highest priority for immediate UX)
  const storedRoles = getStoredActiveRoles();
  const storedRole = storedRoles[accountId];
  if (storedRole && availableRoles.includes(storedRole)) {
    return storedRole;
  }

  // 2. Check server-side preference
  const serverRole = preferences?.activeRoleByAccount?.[accountId];
  if (serverRole && availableRoles.includes(serverRole)) {
    return serverRole;
  }

  // 3. Fall back to claims.role if it's in available roles
  if (claimsRole && availableRoles.includes(claimsRole)) {
    return claimsRole;
  }

  // 4. Fall back to first available role
  return availableRoles[0] || null;
}

/**
 * Role display labels
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  owner: 'Owner',
  admin: 'Administrator',
  supervisor: 'Monitor',
  parent: 'Parent',
};

/**
 * Get the display label for a role
 */
export function getRoleLabel(role: UserRole): string {
  return ROLE_LABELS[role] || role;
}
